import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, User, Phone, MapPin, DollarSign, Save, Loader2, AlertCircle, Wrench, Clock } from 'lucide-react';
import { ServiceAdvisorService } from '../../services/serviceAdvisorService';
import { MechanicLogsService } from '../../services/mechanicLogsService';

const AssignmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  
  // Get selected mechanic from location state (if navigated from AssignMechanic screen)
  const selectedMechanicFromState = location.state?.selectedMechanic;
  const complaintFromState = location.state?.complaint;
  
  const [selectedMechanicId, setSelectedMechanicId] = useState(selectedMechanicFromState?.id || '');
  const [mechanicPayment, setMechanicPayment] = useState('');
  const [assignmentRemark, setAssignmentRemark] = useState('');
  const [dispatchRemark, setDispatchRemark] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  const { data: complaintData, isLoading: isLoadingComplaint } = useQuery({
    queryKey: ['serviceAdvisorComplaintDetail', id],
    queryFn: async () => {
      const result = await ServiceAdvisorService.getComplaintById(id);
      return result.data;
    },
    enabled: !!id,
  });

  const { data: mechanicsData, isLoading: isLoadingMechanics } = useQuery({
    queryKey: ['serviceAdvisorMechanicsForAssign', complaintData?.pincode, complaintData?.service_type],
    queryFn: () => ServiceAdvisorService.getAvailableMechanics(
      complaintData?.service_type || null,
      null, // location
      complaintData?.pincode || null
    ),
    enabled: !!id && !!complaintData,
  });

  const complaint = complaintData || complaintFromState || {};
  const mechanics = mechanicsData?.data || [];
  const selectedMechanic = selectedMechanicFromState || mechanics.find((m) => m.id === selectedMechanicId);
  
  // Update selectedMechanicId if we have one from state
  useEffect(() => {
    if (selectedMechanicFromState?.id) {
      setSelectedMechanicId(selectedMechanicFromState.id);
    }
  }, [selectedMechanicFromState]);

  const handleAssign = async () => {
    if (!selectedMechanicId) {
      alert('Please select a mechanic');
      return;
    }

    if (!selectedMechanic) {
      alert('Selected mechanic not found');
      return;
    }

    setIsAssigning(true);
    try {
      const logsService = MechanicLogsService.getInstance();
      
      // Use assignMechanicToRequest with all parameters exactly like mobile app
      const result = await ServiceAdvisorService.assignMechanicToRequest(
        id,
        selectedMechanicId,
        selectedMechanic.name || selectedMechanic.mechanic_name || 'Unknown',
        selectedMechanic.contact_number || selectedMechanic.phone || '',
        dispatchRemark || null,
        mechanicPayment ? parseFloat(mechanicPayment) : null,
        assignmentRemark || null
      );

      if (result.success) {
        // Log successful assignment
        await logsService.addLog({
          mechanicId: selectedMechanicId,
          mechanicName: selectedMechanic.name || selectedMechanic.mechanic_name || 'Unknown',
          action: 'assign',
          details: `Successfully assigned mechanic to complaint ${id}`,
          userId: user?.id,
          userName: user?.name || user?.email || 'Unknown',
          complaintId: id,
          phoneNumber: selectedMechanic.contact_number || selectedMechanic.phone
        });
        
        alert(result.message || 'Mechanic assigned successfully!');
        queryClient.invalidateQueries(['serviceAdvisorComplaintDetail', id]);
        queryClient.invalidateQueries(['serviceAdvisorComplaints', user?.id]);
        navigate(-1);
      } else {
        // Log failed assignment
        await logsService.addLog({
          mechanicId: selectedMechanicId,
          mechanicName: selectedMechanic.name || selectedMechanic.mechanic_name || 'Unknown',
          action: 'assign',
          details: `Failed to assign mechanic: ${result.error || result.message || 'Unknown error'}`,
          userId: user?.id,
          userName: user?.name || user?.email || 'Unknown',
          complaintId: id,
          phoneNumber: selectedMechanic.contact_number || selectedMechanic.phone
        });
        
        alert('Failed to assign mechanic: ' + (result.error || result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error assigning mechanic:', error);
      alert('Error assigning mechanic: ' + error.message);
    } finally {
      setIsAssigning(false);
    }
  };

  if (isLoadingComplaint) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  if (!complaint || !complaint.id) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-6 bg-white rounded-lg shadow-md">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Complaint Not Found</h2>
          <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center space-x-4 mb-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Assign Mechanic</h1>
          <p className="text-gray-600">Complaint #{complaint.id?.slice(0, 8)}</p>
        </div>
      </div>

      {/* Complaint Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Complaint Summary</h2>
          {complaint.contact_number && (
            <button
              onClick={() => window.location.href = `tel:${complaint.contact_number}`}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-2"
            >
              <Phone size={18} />
              <span>Call Customer</span>
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Customer Name</p>
            <p className="font-medium text-gray-900">{complaint.customer_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Contact Number</p>
            <a href={`tel:${complaint.contact_number}`} className="font-medium text-gray-900 hover:text-teal-600">
              {complaint.contact_number || 'N/A'}
            </a>
          </div>
          <div className="md:col-span-2">
            <p className="text-sm text-gray-500">Address</p>
            <p className="font-medium text-gray-900">{complaint.address || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Pincode</p>
            <p className="font-medium text-gray-900">{complaint.pincode || 'Not set'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Service Type</p>
            <p className="font-medium text-gray-900">{complaint.service_type || 'N/A'}</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-sm text-gray-500">Problem</p>
            <p className="font-medium text-gray-900">{complaint.problem || complaint.description || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Assignment Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
            <Wrench size={24} />
            <span>Mechanic Assignment</span>
          </h2>
          {selectedMechanic && (selectedMechanic.contact_number || selectedMechanicFromState?.contact_number) && (
            <button
              onClick={async () => {
                try {
                  const logsService = MechanicLogsService.getInstance();
                  const phoneNumber = selectedMechanic.contact_number || selectedMechanicFromState.contact_number;
                  
                  // Log the call action
                  await logsService.addLog({
                    mechanicId: selectedMechanic.id || selectedMechanicFromState.id,
                    mechanicName: selectedMechanic.name || selectedMechanic.mechanic_name || selectedMechanicFromState.name || 'Unknown',
                    action: 'call',
                    details: `Called mechanic: ${phoneNumber}`,
                    userId: user?.id,
                    userName: user?.name || user?.email || 'Unknown',
                    complaintId: id,
                    phoneNumber: phoneNumber
                  });
                  
                  window.location.href = `tel:${phoneNumber}`;
                } catch (error) {
                  console.error('Error logging call:', error);
                  const phoneNumber = selectedMechanic.contact_number || selectedMechanicFromState.contact_number;
                  window.location.href = `tel:${phoneNumber}`;
                }
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-2"
            >
              <Phone size={18} />
              <span>Call Mechanic</span>
            </button>
          )}
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Mechanic *</label>
            {selectedMechanicFromState ? (
              <div className="p-4 bg-teal-50 border-2 border-teal-500 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <User size={20} className="text-teal-600" />
                      <span className="text-lg font-semibold text-gray-900">
                        {selectedMechanicFromState.name || selectedMechanicFromState.mechanic_name || 'Unknown'}
                      </span>
                    </div>
                    {selectedMechanicFromState.contact_number && (
                      <div className="flex items-center space-x-2 mb-1">
                        <Phone size={16} className="text-gray-400" />
                        <a href={`tel:${selectedMechanicFromState.contact_number}`} className="text-sm text-gray-600 hover:text-teal-600">
                          {selectedMechanicFromState.contact_number}
                        </a>
                      </div>
                    )}
                    {(selectedMechanicFromState.timing || selectedMechanicFromState.timings) && (
                      <div className="flex items-center space-x-2 mb-1">
                        <Clock size={16} className="text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Timing: {selectedMechanicFromState.timing || selectedMechanicFromState.timings}
                        </span>
                      </div>
                    )}
                    {selectedMechanicFromState.pincode && (
                      <div className="flex items-center space-x-2">
                        <MapPin size={16} className="text-gray-400" />
                        <span className="text-sm text-gray-600">Pincode: {selectedMechanicFromState.pincode}</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`/service_advisor/assign-mechanic/${id}`, {
                      state: { complaint: complaint }
                    })}
                    className="px-3 py-1 text-sm text-teal-600 hover:text-teal-700 underline"
                  >
                    Change
                  </button>
                </div>
              </div>
            ) : (
              <>
            <select
                  value={selectedMechanicId}
                  onChange={(e) => setSelectedMechanicId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Select mechanic...</option>
              {mechanics.map((mechanic) => (
                <option key={mechanic.id} value={mechanic.id}>
                      {mechanic.name || mechanic.mechanic_name || 'Unknown'} - {mechanic.contact_number || mechanic.number || 'N/A'}
                      {(mechanic.timing || mechanic.timings) ? ` (Timing: ${mechanic.timing || mechanic.timings})` : ''}
                      {mechanic.pincode && ` (Pincode: ${mechanic.pincode})`}
                </option>
              ))}
            </select>
                {!selectedMechanicId && (
                  <button
                    onClick={() => navigate(`/service_advisor/assign-mechanic/${id}`, {
                      state: { complaint: complaint }
                    })}
                    className="mt-2 w-full px-4 py-2 border-2 border-dashed border-teal-300 text-teal-600 rounded-lg hover:bg-teal-50"
                  >
                    Browse All Mechanics
                  </button>
                )}
                {selectedMechanic && !selectedMechanicFromState && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <User size={16} className="text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">
                        {selectedMechanic.name || selectedMechanic.mechanic_name}
                      </span>
                    </div>
                    {selectedMechanic.contact_number && (
                      <div className="flex items-center space-x-2 mt-1">
                        <Phone size={16} className="text-gray-400" />
                        <a href={`tel:${selectedMechanic.contact_number}`} className="text-sm text-gray-600 hover:text-teal-600">
                          {selectedMechanic.contact_number}
                        </a>
                      </div>
                    )}
                    {(selectedMechanic.timing || selectedMechanic.timings) && (
                      <div className="flex items-center space-x-2 mt-1">
                        <Clock size={16} className="text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Timing: {selectedMechanic.timing || selectedMechanic.timings}
                        </span>
                      </div>
                    )}
                    {selectedMechanic.pincode && (
                      <div className="flex items-center space-x-2 mt-1">
                        <MapPin size={16} className="text-gray-400" />
                        <span className="text-sm text-gray-600">Pincode: {selectedMechanic.pincode}</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mechanic Payment Amount (₹)
            </label>
            <input
              type="number"
              value={mechanicPayment}
              onChange={(e) => setMechanicPayment(e.target.value)}
              placeholder="Enter payment amount (optional)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dispatch Remark
            </label>
            <textarea
              value={dispatchRemark}
              onChange={(e) => setDispatchRemark(e.target.value)}
              rows={3}
              placeholder="Add dispatch remarks (optional)..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assignment Remark
            </label>
            <textarea
              value={assignmentRemark}
              onChange={(e) => setAssignmentRemark(e.target.value)}
              rows={4}
              placeholder="Add assignment remarks or notes (optional)..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              onClick={() => navigate(-1)}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedMechanicId || isAssigning}
              className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {isAssigning ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Assigning...</span>
                </>
              ) : (
                <>
                  <Save size={18} />
              <span>Assign Mechanic</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentDetails;
